import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Register() {
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "player" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", form);
      setUser(data);
      toast.success("Account created");
      nav(data.role === "coach" ? "/coach" : "/profile");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-baseline gap-2 justify-center mb-10" data-testid="register-logo">
          <span className="font-heading font-black text-3xl uppercase">Gainline</span>
          <span className="font-heading font-black text-3xl text-[#FF3B30] uppercase">Performance</span>
        </Link>
        <div className="border border-[#262626] bg-[#121212] p-8">
          <h1 className="font-heading font-black uppercase text-3xl mb-1">Create Account</h1>
          <p className="text-sm text-[#A3A3A3] mb-8">Free to start. No card required.</p>

          <div className="flex gap-2 mb-6">
            <button type="button" onClick={() => setForm({ ...form, role: "player" })} data-testid="role-player"
              className={`flex-1 py-3 border font-heading uppercase tracking-widest text-sm ${form.role === "player" ? "border-[#FF3B30] bg-[#FF3B30]/10 text-white" : "border-[#262626] text-[#A3A3A3] hover:text-white"}`}>
              Player
            </button>
            <button type="button" onClick={() => setForm({ ...form, role: "coach" })} data-testid="role-coach"
              className={`flex-1 py-3 border font-heading uppercase tracking-widest text-sm ${form.role === "coach" ? "border-[#FF3B30] bg-[#FF3B30]/10 text-white" : "border-[#262626] text-[#A3A3A3] hover:text-white"}`}>
              Coach
            </button>
          </div>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label className="font-heading uppercase tracking-widest text-xs">Full Name</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="register-name"
                className="mt-2 rounded-none bg-[#0A0A0A] border-[#262626] focus-visible:ring-0 focus:border-[#FF3B30] h-11" />
            </div>
            <div>
              <Label className="font-heading uppercase tracking-widest text-xs">Email</Label>
              <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="register-email"
                className="mt-2 rounded-none bg-[#0A0A0A] border-[#262626] focus-visible:ring-0 focus:border-[#FF3B30] h-11" />
            </div>
            <div>
              <Label className="font-heading uppercase tracking-widest text-xs">Password</Label>
              <Input type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="register-password"
                className="mt-2 rounded-none bg-[#0A0A0A] border-[#262626] focus-visible:ring-0 focus:border-[#FF3B30] h-11" />
            </div>
            <Button type="submit" disabled={loading} data-testid="register-submit"
              className="w-full rounded-none bg-[#FF3B30] hover:bg-[#D63026] font-heading uppercase tracking-widest font-bold h-11">
              {loading ? "Creating…" : "Create Account"}
            </Button>
          </form>
          <div className="mt-6 text-sm text-[#A3A3A3] text-center">
            Already registered? <Link to="/login" className="text-[#FF3B30] font-heading uppercase tracking-widest text-xs" data-testid="register-to-login">Sign In</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
